import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CandlestickData } from 'lightweight-charts';
import BacktestChart from '../../components/BacktestChart/BacktestChart';
import TradesTable from '../../components/TradesTable/TradesTable';
import DataAvailability from '../../components/DataAvailability/DataAvailability';
import { useBacktestStore } from '../../stores/backtestStore';
import { getApiUrl } from '../../config/api';
import './Backtest.css';

interface Trade {
  id: string;
  time: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  pnl?: number;
  reason?: string;
  commission?: number;
}

interface GridLevel {
  price: number;
  active: boolean;
}

const Backtest: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [results, setResults] = useState<any>(null);
  const [chartData, setChartData] = useState<CandlestickData[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [gridLevels, setGridLevels] = useState<GridLevel[]>([]);
  const [currentTime, setCurrentTime] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get parameters from navigation state
  const stateParams = location.state as any;

  // Persisted form state (survives navigation)
  const {
    symbol, setSymbol,
    strategy, setStrategy,
    timeframe, setTimeframe,
    startDate, setStartDate,
    endDate, setEndDate,
    capital, setCapital,
    stopLoss, setStopLoss,
    takeProfit, setTakeProfit,
    gridSize, setGridSize,
    priceRange, setPriceRange,
    orderQuantity, setOrderQuantity,
    lotSize, setLotSize,
    updateInterval, setUpdateInterval,
    minProfit, setMinProfit,
  } = useBacktestStore();

  // Initialize form with data from Trading page
  useEffect(() => {
    if (stateParams) {
      if (stateParams.future) setSymbol(stateParams.future);
      if (stateParams.strategy) setStrategy(stateParams.strategy);
      if (stateParams.capital) setCapital(stateParams.capital.toString());
      if (stateParams.stop_loss) setStopLoss(stateParams.stop_loss.toString());
      if (stateParams.take_profit) setTakeProfit(stateParams.take_profit.toString());

      // Grid Bot specific parameters
      if (stateParams.strategy_config) {
        const config = stateParams.strategy_config;
        if (config.grid_size) setGridSize(config.grid_size.toString());
        if (config.price_range) setPriceRange(config.price_range.toString());
        if (config.order_quantity) setOrderQuantity(config.order_quantity.toString());
        if (config.lot_size) setLotSize(config.lot_size.toString());
        if (config.update_interval) setUpdateInterval(config.update_interval.toString());
        if (config.min_profit) setMinProfit(config.min_profit.toString());
      }
    }
  }, [stateParams]);

  const runBacktest = async () => {
    if (!symbol) {
      setError('Укажите символ актива');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);
    setChartData([]);
    setTrades([]);
    setGridLevels([]);

    try {
      const response = await fetch(getApiUrl('/backtest/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          broker: 'TBANK',
          symbol,
          interval: timeframe,
          start_date: startDate,
          end_date: endDate,
          initial_capital: parseFloat(capital),
          strategy,
          stop_loss: parseFloat(stopLoss),
          take_profit: parseFloat(takeProfit),
          grid_size: parseInt(gridSize),
          price_range: parseFloat(priceRange),
          order_quantity: parseInt(orderQuantity),
          lot_size: parseInt(lotSize),
          min_profit: parseFloat(minProfit),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || `Server error: ${response.status}`);
        setIsLoading(false);
        return;
      }

      // Map candles to chart format
      const candles: CandlestickData[] = (data.candles || []).map((c: any) => ({
        time: c.time as any,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }));

      setChartData(candles);
      setTrades(data.trades || []);
      setGridLevels(data.grid_levels || []);

      setResults({
        totalReturn: data.total_return,
        sharpeRatio: data.sharpe_ratio,
        maxDrawdown: data.max_drawdown,
        winRate: data.win_rate,
        totalTrades: data.total_trades,
        totalPnL: data.total_pnl,
        finalCapital: data.final_capital,
        profitFactor: data.profit_factor,
        winningTrades: data.winning_trades,
        losingTrades: data.losing_trades,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to run backtest');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('backtest.title')}</h1>
      </div>

      <div className="backtest-container">
        {/* Data Availability Check */}
        {symbol && (
          <DataAvailability
            broker="TBANK"
            symbol={symbol}
            onDataLoaded={() => console.log('Data loaded for', symbol)}
          />
        )}

        <div className="grid grid-2">
          <div className="card">
            <h2>Параметры бэктеста</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label>{t('backtest.symbol')}</label>
                <input
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  placeholder="FIGI актива"
                />
              </div>
              <div>
                <label>Стратегия</label>
                <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
                  <option value="grid_bot">Grid Bot (Сеточная торговля)</option>
                </select>
              </div>
              <div>
                <label>{t('backtest.timeframe')}</label>
                <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                  <option value="1m">1 Minute</option>
                  <option value="1h">1 Hour</option>
                  <option value="1d">1 Day</option>
                </select>
              </div>
              <div>
                <label>{t('backtest.start_date')}</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label>{t('backtest.end_date')}</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div>
                <label>{t('backtest.initial_capital')}</label>
                <input
                  type="number"
                  value={capital}
                  onChange={(e) => setCapital(e.target.value)}
                />
              </div>
              <div>
                <label>Stop Loss (%)</label>
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  step="0.1"
                />
              </div>
              <div>
                <label>Take Profit (%)</label>
                <input
                  type="number"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  step="0.1"
                />
              </div>

              {strategy === 'grid_bot' && (
                <div className="grid-bot-params">
                  <h3>Параметры Grid Bot</h3>
                  <div className="grid-bot-params-grid">
                    <div>
                      <label>Размер сетки (уровней)</label>
                      <input
                        type="number"
                        value={gridSize}
                        onChange={(e) => setGridSize(e.target.value)}
                      />
                      <small>
                        Количество уровней цен (3-50)
                      </small>
                    </div>
                    <div>
                      <label>Диапазон цен (%)</label>
                      <input
                        type="number"
                        value={priceRange}
                        onChange={(e) => setPriceRange(e.target.value)}
                        step="0.1"
                      />
                      <small>
                        Диапазон от текущей цены (1-20%)
                      </small>
                    </div>
                    <div>
                      <label>Объем ордера (лотов)</label>
                      <input
                        type="number"
                        value={orderQuantity}
                        onChange={(e) => setOrderQuantity(e.target.value)}
                      />
                      <small>
                        Количество лотов на ордер
                      </small>
                    </div>
                    <div>
                      <label>Размер лота (единиц)</label>
                      <input
                        type="number"
                        value={lotSize}
                        onChange={(e) => setLotSize(e.target.value)}
                      />
                      <small>
                        USDRUBF = 1000, Si = 1000
                      </small>
                    </div>
                    <div>
                      <label>Интервал обновления (сек)</label>
                      <input
                        type="number"
                        value={updateInterval}
                        onChange={(e) => setUpdateInterval(e.target.value)}
                      />
                      <small>
                        Частота проверки сетки (30-300)
                      </small>
                    </div>
                    <div>
                      <label>Мин. прибыль (%)</label>
                      <input
                        type="number"
                        value={minProfit}
                        onChange={(e) => setMinProfit(e.target.value)}
                        step="0.1"
                      />
                      <small>
                        Минимальная прибыль на сделку (0.1-5%)
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="backtest-error">
                  {error}
                </div>
              )}

              <button
                className="primary"
                onClick={runBacktest}
                disabled={isLoading || !symbol}
              >
                {isLoading ? 'Выполняется...' : 'Запустить бэктест'}
              </button>
            </div>
          </div>

          {results && (
            <div className="card">
              <h2>Результаты</h2>
              <div className="backtest-results">
                <div className="result-item">
                  <div className="result-label">Общая доходность</div>
                  <div className={`result-value ${parseFloat(results.totalReturn) >= 0 ? 'positive' : 'negative'}`}>
                    {parseFloat(results.totalReturn) >= 0 ? '+' : ''}{results.totalReturn}%
                  </div>
                </div>
                <div className="result-item">
                  <div className="result-label">PnL</div>
                  <div className={`result-value ${parseFloat(results.totalPnL) >= 0 ? 'positive' : 'negative'}`}>
                    {parseFloat(results.totalPnL) >= 0 ? '+' : ''}{results.totalPnL}
                  </div>
                </div>
                <div className="result-item">
                  <div className="result-label">Итоговый капитал</div>
                  <div className="result-value">{results.finalCapital}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Sharpe Ratio</div>
                  <div className="result-value">{results.sharpeRatio}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Max Drawdown</div>
                  <div className="result-value negative">
                    {results.maxDrawdown}%
                  </div>
                </div>
                <div className="result-item">
                  <div className="result-label">Win Rate</div>
                  <div className="result-value">{results.winRate}%</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Profit Factor</div>
                  <div className="result-value">{results.profitFactor}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Всего сделок</div>
                  <div className="result-value">
                    {results.totalTrades} ({results.winningTrades}W / {results.losingTrades}L)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {results && chartData.length > 0 && (
          <>
            <div className="card">
              <h2>График и визуализация</h2>
              <BacktestChart
                data={chartData}
                trades={trades}
                gridLevels={gridLevels}
                onTimeChange={setCurrentTime}
              />
            </div>

            <div className="card">
              <h2>История сделок</h2>
              <TradesTable trades={trades} currentTime={currentTime} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Backtest;
